import { Card, CardBody, Button, Select, Input } from '@components/ui';
import type { InvoiceWizardData } from '../../types/invoiceWizard.types';
import { useState, useRef, useEffect } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatNumber, round2 } from '@/utils/format';
import { showWarning } from '@/utils/sweetAlert';

interface Step4AdditionalInfoProps {
  wizardData: InvoiceWizardData;
  onUpdate: (updates: Partial<InvoiceWizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const RETENTION_TYPES = [
  { value: '0', label: 'No aplica' },
  { value: '1', label: 'Pago por servicio profesional al estado 100%' },
  { value: '2', label: 'Pago por venta de bienes/servicios al estado 50%' },
  { value: '3', label: 'Pago o acreditación a no domiciliado o empresa constituida en el exterior 100%' },
  { value: '4', label: 'Pago o acreditación por compra de bienes/servicios 50%' },
  { value: '7', label: 'Pago a comercio afiliado a sistema de TC/TD 50%' },
  { value: '8', label: 'Otros (disminución de la retención)' },
];

const PAYMENT_METHODS = [
  { value: '01', label: 'Crédito' },
  { value: '02', label: 'Efectivo' },
  { value: '03', label: 'Tarjeta Crédito' },
  { value: '04', label: 'Tarjeta Débito' },
  { value: '05', label: 'Tarjeta Fidelización' },
  { value: '06', label: 'Vale' },
  { value: '07', label: 'Tarjeta de Regalo' },
  { value: '08', label: 'Transf./Depósito a cta. Bancaria' },
  { value: '09', label: 'Cheque' },
  { value: '10', label: 'Punto de Pago' },
  { value: '99', label: 'Otro' },
];

export function Step4AdditionalInfo({ wizardData, onUpdate, onNext, onPrevious }: Step4AdditionalInfoProps) {
  const globalOrderDetailsRef = useRef<HTMLDetailsElement>(null);
  const globalOrderNumberRef = useRef<HTMLInputElement>(null);
  const [newDiscountDesc, setNewDiscountDesc] = useState('');
  const [newDiscountAmount, setNewDiscountAmount] = useState('');

  // Estados para nuevo pago
  const [newPaymentMethod, setNewPaymentMethod] = useState('02'); // 02 = Efectivo
  const [newPaymentAmount, setNewPaymentAmount] = useState('');
  const [newPaymentDescription, setNewPaymentDescription] = useState('');

  // Estados para nuevo plazo de pago
  const [newTermDate, setNewTermDate] = useState('');
  const [newTermAmount, setNewTermAmount] = useState('');
  const [newTermDescription, setNewTermDescription] = useState('');

  // Estados locales para acarreo y seguro (permite escribir libremente, formatea al salir)
  const [shippingInput, setShippingInput] = useState(wizardData.shippingCost ? Number(wizardData.shippingCost).toFixed(2) : '');
  const [insuranceInput, setInsuranceInput] = useState(wizardData.insuranceCost ? Number(wizardData.insuranceCost).toFixed(2) : '');

  // Validación: si llena cualquier campo de pedido global, todos son obligatorios
  const hasAnyGlobalOrderField = !!(wizardData.globalOrderNumber || wizardData.globalOrderAcceptanceNumber || wizardData.globalOrderReceiverCode || wizardData.globalOrderIssuerSystemCode || wizardData.globalOrderInfo);

  const globalOrderErrors: Record<string, string> = {};
  if (hasAnyGlobalOrderField) {
    if (!wizardData.globalOrderNumber) globalOrderErrors.globalOrderNumber = 'Requerido';
    if (!wizardData.globalOrderAcceptanceNumber) globalOrderErrors.globalOrderAcceptanceNumber = 'Requerido';
    if (!wizardData.globalOrderReceiverCode) globalOrderErrors.globalOrderReceiverCode = 'Requerido';
    if (!wizardData.globalOrderIssuerSystemCode) globalOrderErrors.globalOrderIssuerSystemCode = 'Requerido';
    if (!wizardData.globalOrderInfo) globalOrderErrors.globalOrderInfo = 'Requerido';
  }
  const hasGlobalOrderErrors = Object.keys(globalOrderErrors).length > 0;

  const handleNext = () => {
    // Validación de pedido global (todo-o-nada)
    if (hasGlobalOrderErrors) {
      if (globalOrderDetailsRef.current) {
        globalOrderDetailsRef.current.open = true;
      }
      setTimeout(() => {
        globalOrderNumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      showWarning('Campos requeridos', 'Si completa un campo de pedido global, debe llenar todos los campos de la sección.');
      return;
    }

    // Validación: debe tener al menos una forma de pago (excepto si el total es B/0.00)
    if (grandTotalRounded > 0 && (!wizardData.payments || wizardData.payments.length === 0)) {
      showWarning('Forma de pago requerida', 'Debe agregar al menos una forma de pago.');
      return;
    }

    // Validación DGI D302: si forma de pago es 99 (Otro), descripción es obligatoria
    const paymentMissingDesc = wizardData.payments.find(p => p.method === '99' && !p.description?.trim());
    if (paymentMissingDesc) {
      showWarning('Descripción requerida', 'La forma de pago "Otro" requiere una descripción (DGI D302).');
      return;
    }

    // Validación: si contado, la suma de pagos debe cubrir el total
    if (wizardData.paymentCondition === '1') {
      if (totalPayments < grandTotalRounded && Math.abs(remainingAmount) > 0.01) {
        showWarning('Monto insuficiente', `La suma de los pagos (B/${formatNumber(totalPayments)}) no cubre el total de la factura (B/${formatNumber(grandTotalRounded)}). Falta B/${formatNumber(remainingAmount)}.`);
        return;
      }
    }

    // Validación DGI D50/D09a: si crédito o mixto, debe haber al menos un plazo de pago
    if (wizardData.paymentCondition !== '1') {
      if (!wizardData.paymentTerms || wizardData.paymentTerms.length === 0) {
        showWarning('Plazo de pago requerido', 'Para pago a crédito o mixto, debe agregar al menos un plazo de pago (DGI D50).');
        return;
      }
      // Validar que la suma de plazos = total factura (DGI D09a)
      const totalTerms = round2((wizardData.paymentTerms || []).reduce((sum, term) => sum + (parseFloat(term.dValItPlazo) || 0), 0));
      if (Math.abs(totalTerms - grandTotalRounded) > 0.50) {
        showWarning('Suma de plazos incorrecta', `La suma de los plazos (B/${formatNumber(totalTerms)}) debe ser igual al total de la factura (B/${formatNumber(grandTotalRounded)}) (DGI D09a).`);
        return;
      }
    }

    // Advertencia DGI D401: si receptor es Gobierno y retención = 0
    if (wizardData.receiverType === '03' && (!wizardData.retention || wizardData.retention === '0')) {
      showWarning('Retención recomendada', 'Las transacciones con entidades del Gobierno generalmente requieren configurar una retención (DGI D401). Puede continuar sin ella si no aplica.');
      // No bloquea, solo advierte - el usuario puede volver a presionar Siguiente
    }

    onNext();
  };

  const handleInputChange = (field: keyof InvoiceWizardData) => (e: React.ChangeEvent<HTMLTextAreaElement | HTMLSelectElement | HTMLInputElement>) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;

    // Auto-limpieza al cambiar condición de pago
    if (field === 'paymentCondition') {
      const updates: Partial<InvoiceWizardData> = { paymentCondition: value as string };
      if (value === '1') {
        // Contado: limpiar plazos de pago (no aplican)
        updates.paymentTerms = [];
      } else if (value === '2') {
        // Crédito: DGI D30 requiere forma de pago, auto-agregar "01 - Crédito" por el total
        updates.payments = [{
          method: '01',
          amount: grandTotalRounded,
          description: '',
        }];
      }
      // Mixto (3): mantiene ambos
      onUpdate(updates);
      return;
    }

    // Limpiar monto manual si cambian de código 8 a otro
    if (field === 'retention' && value !== '8') {
      onUpdate({ retention: value as string, retentionAmount: 0 });
      return;
    }

    onUpdate({ [field]: value });
  };

  // Determinar si es una nota de crédito/débito
  const isNote = wizardData.documentMode === 'credit-note' || wizardData.documentMode === 'debit-note';
  const isGobierno = wizardData.receiverType === '03';

  // DGI C204/D07 y C205/D08: Detectar si hay acarreo/seguro en ítems
  const hasItemShipping = wizardData.items?.some(item => (item.shippingAmount || 0) > 0) || false;
  const hasItemInsurance = wizardData.items?.some(item => (item.insuranceAmount || 0) > 0) || false;

  // Calcular total de pagos agregados (usando round2 para evitar errores de punto flotante)
  const totalPayments = round2((wizardData.payments || []).reduce((sum, payment) => sum + (payment.amount || 0), 0));
  const grandTotalRounded = round2(wizardData.grandTotal || 0);
  const remainingAmount = round2(grandTotalRounded - totalPayments);

  // En notas de crédito/débito NO hay vuelto, solo el monto exacto
  const changeAmount = isNote ? 0 : (totalPayments > grandTotalRounded ? round2(totalPayments - grandTotalRounded) : 0);

  // Sincronizar payment auto-agregado cuando es Crédito y cambia el grandTotal
  useEffect(() => {
    if (wizardData.paymentCondition === '2' && grandTotalRounded > 0) {
      const currentPayment = wizardData.payments?.[0];
      if (!currentPayment || currentPayment.method !== '01' || currentPayment.amount !== grandTotalRounded) {
        onUpdate({
          payments: [{
            method: '01',
            amount: grandTotalRounded,
            description: '',
          }],
        });
      }
    }
  }, [wizardData.paymentCondition, grandTotalRounded]);

  // Agregar un descuento global
  const handleAddGlobalDiscount = () => {
    if (!newDiscountDesc.trim() || !newDiscountAmount || parseFloat(newDiscountAmount) <= 0) {
      return;
    }

    const newDiscount = {
      description: newDiscountDesc.trim(),
      amount: parseFloat(newDiscountAmount),
    };

    onUpdate({
      globalDiscounts: [...(wizardData.globalDiscounts || []), newDiscount],
    });

    // Limpiar los campos
    setNewDiscountDesc('');
    setNewDiscountAmount('');
  };

  // Eliminar un descuento global
  const handleRemoveGlobalDiscount = (index: number) => {
    const updatedDiscounts = wizardData.globalDiscounts.filter((_, i) => i !== index);
    onUpdate({ globalDiscounts: updatedDiscounts });
  };

  // Agregar una forma de pago
  const handleAddPayment = () => {
    const amount = parseFloat(newPaymentAmount);
    if (!amount || amount <= 0) {
      return;
    }

    // Si el método de pago es "99" (Otro), la descripción es requerida
    if (newPaymentMethod === '99' && !newPaymentDescription.trim()) {
      showWarning(
        'Descripción Requerida',
        'Debe especificar el método de pago cuando selecciona "Otro"'
      );
      return;
    }

    // Validar longitud mínima de 10 caracteres en la descripción
    if (newPaymentMethod === '99' && newPaymentDescription.trim().length < 10) {
      showWarning(
        'Descripción Muy Corta',
        'La descripción debe tener al menos 10 caracteres'
      );
      return;
    }

    // Para notas de crédito/débito: NO permitir exceder el total (no hay vuelto)
    // Validar que la suma de todos los pagos no exceda el grand total
    // Usar round2 para evitar problemas de precisión de punto flotante
    const roundedTotal = round2(totalPayments + amount);
    const roundedGrandTotal = round2(wizardData.grandTotal);

    if (isNote && roundedTotal > roundedGrandTotal) {
      const maxAllowed = wizardData.grandTotal - totalPayments;
      showWarning(
        'Monto Excedido',
        `En notas de crédito/débito el total de pagos no puede exceder el monto. Máximo permitido: $${maxAllowed.toFixed(2)}`
      );
      return;
    }

    // Para facturas: solo efectivo (método '02') permite exceder el total (para calcular vuelto)
    // Para otros métodos de pago: no permitir exceder el total pendiente
    // Usar round2 para evitar problemas de precisión de punto flotante
    const roundedAmount = round2(amount);
    const roundedRemaining = round2(remainingAmount);

    if (!isNote && newPaymentMethod !== '02' && roundedAmount > roundedRemaining) {
      showWarning(
        'Monto Excedido',
        `El monto no puede exceder el total pendiente: $${remainingAmount.toFixed(2)}`
      );
      return;
    }

    // DGI D302a (2602): Solo incluir descripción cuando método = 99 (Otro)
    const newPayment = {
      method: newPaymentMethod,
      amount: amount,
      description: newPaymentMethod === '99' ? newPaymentDescription.trim() : undefined,
    };

    onUpdate({
      payments: [...(wizardData.payments || []), newPayment],
    });

    // Limpiar los campos
    setNewPaymentAmount('');
    setNewPaymentDescription('');
    setNewPaymentMethod('01');
  };

  // Eliminar una forma de pago
  const handleRemovePayment = (index: number) => {
    const updatedPayments = (wizardData.payments || []).filter((_, i) => i !== index);
    onUpdate({ payments: updatedPayments });
  };

  // Agregar un plazo de pago
  const handleAddPaymentTerm = () => {
    const amount = parseFloat(newTermAmount);
    if (!newTermDate || !amount || amount <= 0) {
      showWarning(
        'Datos Incompletos',
        'Debe especificar la fecha y el monto del plazo'
      );
      return;
    }

    // Formatear la fecha al formato ISO con timezone
    const formatDateForAPI = (date: string): string => {
      const [year, month, day] = date.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day);

      const now = new Date();
      dateObj.setHours(now.getHours(), now.getMinutes(), now.getSeconds());

      const offset = -dateObj.getTimezoneOffset();
      const offsetHours = Math.floor(Math.abs(offset) / 60).toString().padStart(2, '0');
      const offsetMinutes = (Math.abs(offset) % 60).toString().padStart(2, '0');
      const offsetSign = offset >= 0 ? '+' : '-';

      const formattedYear = dateObj.getFullYear();
      const formattedMonth = (dateObj.getMonth() + 1).toString().padStart(2, '0');
      const formattedDay = dateObj.getDate().toString().padStart(2, '0');
      const hours = dateObj.getHours().toString().padStart(2, '0');
      const minutes = dateObj.getMinutes().toString().padStart(2, '0');
      const seconds = dateObj.getSeconds().toString().padStart(2, '0');

      return `${formattedYear}-${formattedMonth}-${formattedDay}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
    };

    const currentTerms = wizardData.paymentTerms || [];
    const newSequence = (currentTerms.length + 1).toString();

    // Auto-generar descripción: "Cuota de plazo 1", "Cuota de plazo 2", etc.
    // Si el usuario escribió algo, usar eso; si no, usar el valor auto-generado
    const autoDescription = `Cuota de plazo ${newSequence}`;
    const userDesc = newTermDescription.trim();

    // DGI D504: Si se informa dInfPagPlazo, debe tener entre 15 y 1000 caracteres
    if (userDesc && userDesc.length < 15) {
      showWarning('Descripción muy corta', 'La descripción del plazo debe tener al menos 15 caracteres (DGI D504).');
      return;
    }

    const finalDescription = userDesc || autoDescription;

    const newTerm = {
      dSecItem: newSequence,
      dFecItPlazo: formatDateForAPI(newTermDate),
      dValItPlazo: amount.toFixed(2),
      dInfPagPlazo: finalDescription,
    };

    onUpdate({
      paymentTerms: [...currentTerms, newTerm],
    });

    // Limpiar los campos
    setNewTermDate('');
    setNewTermAmount('');
    setNewTermDescription('');
  };

  // Eliminar un plazo de pago
  const handleRemovePaymentTerm = (index: number) => {
    const updatedTerms = (wizardData.paymentTerms || []).filter((_, i) => i !== index);
    // Re-secuenciar los items y actualizar descripciones auto-generadas
    const resequencedTerms = updatedTerms.map((term, i) => {
      const newSequence = (i + 1).toString();
      const oldAutoDescription = `Cuota de plazo ${term.dSecItem}`;

      // Si la descripción era auto-generada, actualizarla con el nuevo número
      // Si no, mantener la descripción personalizada del usuario
      const newDescription = term.dInfPagPlazo === oldAutoDescription
        ? `Cuota de plazo ${newSequence}`
        : term.dInfPagPlazo;

      return {
        ...term,
        dSecItem: newSequence,
        dInfPagPlazo: newDescription,
      };
    });
    onUpdate({ paymentTerms: resequencedTerms });
  };

  // Calcular total de descuentos globales
  const totalGlobalDiscounts = (wizardData.globalDiscounts || []).reduce(
    (sum, disc) => sum + disc.amount,
    0
  );

  // Ya no se usa el handler de amountPaid porque ahora manejamos múltiples pagos

  // Formatear moneda
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: wizardData.currency || 'USD',
    }).format(amount);
  };

  return (
    <div className="space-y-6" data-testid="invoice-wizard-step4-container">
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">Información Adicional y Pago</h2>
        <p className="mt-2 text-secondary-600 dark:text-secondary-400">
          Configura los detalles de pago, totales y observaciones
        </p>
      </div>

      {/* Layout de 2 columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* COLUMNA IZQUIERDA */}
        <div className="space-y-5">
          {/* Grupo: Configuración General */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4">
                Configuración General
              </h3>
              <div className="space-y-4">
                <Select
                  label="Moneda"
                  value={wizardData.currency}
                  onChange={handleInputChange('currency')}
                  options={[
                    { value: 'USD', label: 'USD - Dólar' },
                    { value: 'PAB', label: 'PAB - Balboa' },
                  ]}
                  data-testid="invoice-wizard-step4-currency-select"
                />
                <div>
                  <Select
                    label="Retención"
                    value={wizardData.retention}
                    onChange={handleInputChange('retention')}
                    data-testid="invoice-wizard-step4-retention-select"
                  >
                    {RETENTION_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                  {isGobierno && wizardData.retention === '0' && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-2 rounded mt-2">
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        <strong>DGI D401:</strong> Ventas al Gobierno generalmente requieren retención.
                        <button
                          type="button"
                          className="ml-2 underline font-semibold text-amber-800 dark:text-amber-200 hover:text-amber-900"
                          onClick={() => onUpdate({ retention: '2' })}
                          data-testid="invoice-wizard-step4-apply-retention-50-button"
                        >
                          Aplicar retención 50% (bienes/servicios)
                        </button>
                      </p>
                    </div>
                  )}
                  {wizardData.retention === '8' && (
                    <div className="mt-2">
                      <Input
                        label="Monto de retención (B/)"
                        type="number"
                        min="0"
                        step="0.01"
                        defaultValue={wizardData.retentionAmount || ''}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => onUpdate({ retentionAmount: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        data-testid="invoice-wizard-step4-retention-amount-input"
                      />
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                        Ingrese el monto en dólares (B/) que se retendrá del ITBMS. Este código solo aplica cuando existe una resolución o autorización de la DGI que permite una retención diferente a los porcentajes estándar (50% o 100%). Ejemplo: si el ITBMS es B/70.00 y la DGI autorizó retener B/10.00, ingrese 10.00.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Grupo: Acarreo y Seguro Globales */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4">
                Acarreo y Seguro Globales
              </h3>
              <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-4">
                Costos de envío y seguro aplicados al total de la factura
              </p>
              {/* DGI C204/D07 y C205/D08: No mezclar acarreo/seguro por ítem con total */}
              {wizardData.items?.some(item => (item.shippingAmount || 0) > 0) && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-3 rounded-lg mb-3">
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    <strong>DGI C204:</strong> Ya existe acarreo en algún ítem. No puede informar acarreo a nivel total simultáneamente.
                  </p>
                </div>
              )}
              {wizardData.items?.some(item => (item.insuranceAmount || 0) > 0) && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-3 rounded-lg mb-3">
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    <strong>DGI C205:</strong> Ya existe seguro en algún ítem. No puede informar seguro a nivel total simultáneamente.
                  </p>
                </div>
              )}
              <div className="space-y-3">
                <Input
                  label="Acarreo Global ($)"
                  type="text"
                  inputMode="decimal"
                  value={shippingInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty, numbers, and decimal point
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setShippingInput(value);
                    }
                  }}
                  onBlur={() => {
                    const value = parseFloat(shippingInput) || 0;
                    const rounded = round2(value);
                    setShippingInput(rounded > 0 ? rounded.toFixed(2) : '');
                    onUpdate({ shippingCost: rounded });
                  }}
                  placeholder="0.00"
                  helperText={hasItemShipping ? 'Bloqueado: ya existe acarreo en ítems (DGI C204/D07)' : 'Costo de envío aplicado al total de la factura (no por ítem)'}
                  disabled={hasItemShipping}
                  data-testid="invoice-wizard-step4-shipping-global-input"
                />
                <Input
                  label="Seguro Global ($)"
                  type="text"
                  inputMode="decimal"
                  value={insuranceInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty, numbers, and decimal point
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setInsuranceInput(value);
                    }
                  }}
                  onBlur={() => {
                    const value = parseFloat(insuranceInput) || 0;
                    const rounded = round2(value);
                    setInsuranceInput(rounded > 0 ? rounded.toFixed(2) : '');
                    onUpdate({ insuranceCost: rounded });
                  }}
                  placeholder="0.00"
                  helperText={hasItemInsurance ? 'Bloqueado: ya existe seguro en ítems (DGI C205/D08)' : 'Costo de seguro aplicado al total de la factura (no por ítem)'}
                  disabled={hasItemInsurance}
                  data-testid="invoice-wizard-step4-insurance-global-input"
                />
              </div>
            </CardBody>
          </Card>

          {/* Grupo: Información de Pedido Global (Opcional) */}
          <Card>
            <CardBody>
              <details ref={globalOrderDetailsRef} className="w-full">
                <summary className="cursor-pointer text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
                  📋 Información de Pedido Global (Opcional)
                </summary>
                <div className="space-y-3 mt-4">
                  <Input
                    ref={globalOrderNumberRef}
                    label="Número de Pedido"
                    defaultValue={wizardData.globalOrderNumber || ''}
                    onBlur={(e) => onUpdate({ globalOrderNumber: e.target.value })}
                    placeholder="Ej: PO-20240001"
                    maxLength={12}
                    required={hasAnyGlobalOrderField}
                    error={globalOrderErrors.globalOrderNumber}
                    helperText={!globalOrderErrors.globalOrderNumber ? "Máximo 12 caracteres alfanuméricos (DGI dNroPed)" : undefined}
                    data-testid="invoice-wizard-step4-global-order-number-input"
                  />
                  <Input
                    label="Número de Aceptación"
                    defaultValue={wizardData.globalOrderAcceptanceNumber || ''}
                    onBlur={(e) => onUpdate({ globalOrderAcceptanceNumber: e.target.value })}
                    placeholder="Número de aceptación del pedido"
                    maxLength={12}
                    required={hasAnyGlobalOrderField}
                    error={globalOrderErrors.globalOrderAcceptanceNumber}
                    helperText={!globalOrderErrors.globalOrderAcceptanceNumber ? "Máximo 12 caracteres (DGI dNumAcept)" : undefined}
                    data-testid="invoice-wizard-step4-global-order-acceptance-number-input"
                  />
                  <Input
                    label="Código del Receptor"
                    defaultValue={wizardData.globalOrderReceiverCode || ''}
                    onBlur={(e) => onUpdate({ globalOrderReceiverCode: e.target.value })}
                    placeholder="Código del receptor"
                    maxLength={50}
                    required={hasAnyGlobalOrderField}
                    error={globalOrderErrors.globalOrderReceiverCode}
                    helperText={!globalOrderErrors.globalOrderReceiverCode ? "Código identificador del receptor del pedido" : undefined}
                    data-testid="invoice-wizard-step4-global-order-receiver-code-input"
                  />
                  <Input
                    label="Código del Sistema Emisor"
                    defaultValue={wizardData.globalOrderIssuerSystemCode || ''}
                    onBlur={(e) => onUpdate({ globalOrderIssuerSystemCode: e.target.value })}
                    placeholder="Código del sistema"
                    maxLength={50}
                    required={hasAnyGlobalOrderField}
                    error={globalOrderErrors.globalOrderIssuerSystemCode}
                    helperText={!globalOrderErrors.globalOrderIssuerSystemCode ? "Código del sistema que emite el pedido" : undefined}
                    data-testid="invoice-wizard-step4-global-order-issuer-system-code-input"
                  />
                  <textarea
                    className="block w-full rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400 dark:placeholder-secondary-500 focus:border-primary-500 focus:ring-primary-500"
                    rows={2}
                    defaultValue={wizardData.globalOrderInfo || ''}
                    onBlur={(e) => onUpdate({ globalOrderInfo: e.target.value })}
                    placeholder="Información adicional del pedido"
                    maxLength={5000}
                    required={hasAnyGlobalOrderField}
                    data-testid="invoice-wizard-step4-global-order-info-textarea"
                  />
                  {globalOrderErrors.globalOrderInfo && (
                    <p className="text-sm text-danger-600" data-testid="invoice-wizard-step4-global-order-info-error">{globalOrderErrors.globalOrderInfo}</p>
                  )}
                  {!globalOrderErrors.globalOrderInfo && (
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">Cualquier información adicional relevante</p>
                  )}
                </div>
              </details>
            </CardBody>
          </Card>

          {/* Grupo: Descuentos Globales */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4">
                Descuentos Globales
              </h3>
              <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-4">
                Descuentos aplicados al total de la factura (no a productos individuales)
              </p>

              {/* Lista de descuentos globales */}
              {wizardData.globalDiscounts && wizardData.globalDiscounts.length > 0 && (
                <div className="space-y-2 mb-4">
                  {wizardData.globalDiscounts.map((discount, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg"
                      data-testid={`invoice-wizard-step4-global-discount-row-${index}`}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-secondary-900 dark:text-white">
                          {discount.description}
                        </p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">
                          {formatCurrency(discount.amount)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveGlobalDiscount(index)}
                        className="p-2 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-lg transition-colors"
                        data-testid={`invoice-wizard-step4-remove-global-discount-button-${index}`}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 border-t border-secondary-200 dark:border-secondary-700">
                    <span className="text-sm font-semibold text-secondary-700 dark:text-secondary-300">
                      Total Descuentos
                    </span>
                    <span className="text-sm font-bold text-danger-600 dark:text-danger-400">
                      -{formatCurrency(totalGlobalDiscounts)}
                    </span>
                  </div>
                </div>
              )}

              {/* Formulario para agregar nuevo descuento */}
              <div className="space-y-3">
                <Input
                  label="Descripción del Descuento"
                  value={newDiscountDesc}
                  onChange={(e) => setNewDiscountDesc(e.target.value)}
                  placeholder="ej: Descuento de compra"
                  maxLength={100}
                  data-testid="invoice-wizard-step4-new-discount-description-input"
                />
                <Input
                  label="Monto del Descuento"
                  type="text"
                  inputMode="decimal"
                  value={newDiscountAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty, numbers, and decimal point
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setNewDiscountAmount(value);
                    }
                  }}
                  onBlur={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    if (value > 0) {
                      setNewDiscountAmount(value.toFixed(2));
                    }
                  }}
                  placeholder="0.00"
                  data-testid="invoice-wizard-step4-new-discount-amount-input"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddGlobalDiscount}
                  className="w-full"
                  disabled={!newDiscountDesc.trim() || !newDiscountAmount || parseFloat(newDiscountAmount) <= 0}
                  data-testid="invoice-wizard-step4-add-global-discount-button"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Agregar Descuento Global
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Grupo: Resumen de Totales */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4">
                Resumen de Totales
              </h3>
              <div className="space-y-3">
                {/* Subtotal */}
                <div className="flex justify-between items-center py-2 border-b border-secondary-200 dark:border-secondary-700">
                  <span className="text-sm text-secondary-700 dark:text-secondary-300">Subtotal</span>
                  <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                    {formatCurrency(wizardData.subtotal)}
                  </span>
                </div>

                {/* Descuento Unitario */}
                {wizardData.totalDiscount > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-secondary-200 dark:border-secondary-700">
                    <span className="text-sm text-secondary-700 dark:text-secondary-300">Descuento (Items)</span>
                    <span className="text-sm font-semibold text-danger-600 dark:text-danger-400">
                      -{formatCurrency(wizardData.totalDiscount)}
                    </span>
                  </div>
                )}

                {/* Descuento Global */}
                {totalGlobalDiscounts > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-secondary-200 dark:border-secondary-700">
                    <span className="text-sm text-secondary-700 dark:text-secondary-300">Descuento Global</span>
                    <span className="text-sm font-semibold text-danger-600 dark:text-danger-400">
                      -{formatCurrency(totalGlobalDiscounts)}
                    </span>
                  </div>
                )}

                {/* ITBMS */}
                <div className="flex justify-between items-center py-2 border-b border-secondary-200 dark:border-secondary-700">
                  <span className="text-sm text-secondary-700 dark:text-secondary-300">ITBMS</span>
                  <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                    {formatCurrency(wizardData.totalTax)}
                  </span>
                </div>

                {/* ISC */}
                {wizardData.totalIsc > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-secondary-200 dark:border-secondary-700">
                    <span className="text-sm text-secondary-700 dark:text-secondary-300">ISC</span>
                    <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                      {formatCurrency(wizardData.totalIsc)}
                    </span>
                  </div>
                )}

                {/* OTI */}
                {wizardData.totalOti > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-secondary-200 dark:border-secondary-700">
                    <span className="text-sm text-secondary-700 dark:text-secondary-300">OTI</span>
                    <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                      {formatCurrency(wizardData.totalOti)}
                    </span>
                  </div>
                )}

                {/* Acarreos */}
                {Number(wizardData.shippingCost) > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-secondary-200 dark:border-secondary-700">
                    <span className="text-sm text-secondary-700 dark:text-secondary-300">Acarreos</span>
                    <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                      {formatCurrency(Number(wizardData.shippingCost))}
                    </span>
                  </div>
                )}

                {/* Seguros */}
                {Number(wizardData.insuranceCost) > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-secondary-200 dark:border-secondary-700">
                    <span className="text-sm text-secondary-700 dark:text-secondary-300">Seguros</span>
                    <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                      {formatCurrency(Number(wizardData.insuranceCost))}
                    </span>
                  </div>
                )}

                {/* Total General */}
                <div className="flex justify-between items-center py-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg px-3 mt-3">
                  <span className="text-base font-bold text-primary-900 dark:text-primary-100">Total</span>
                  <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                    {formatCurrency(wizardData.grandTotal)}
                  </span>
                </div>

                {/* DGI D09b (2509): Total > 1 millón → Notificación */}
                {wizardData.grandTotal > 1000000 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-3 rounded-lg mt-3">
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      <strong>Aviso DGI D09b:</strong> El total supera B/1,000,000. La DGI generará una notificación al recibir esta factura.
                    </p>
                  </div>
                )}

                {/* DGI D09c (2515): Consumidor Final + total > 10 mil → Notificación */}
                {wizardData.receiverType === '02' && wizardData.grandTotal > 10000 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-3 rounded-lg mt-3">
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      <strong>Aviso DGI D09c:</strong> Factura a Consumidor Final con total mayor a B/10,000. La DGI generará una notificación.
                    </p>
                  </div>
                )}

                {/* DGI D402 (2651): Monto de retención informativo */}
                {wizardData.retention && wizardData.retention !== '0' && wizardData.totalTax > 0 && (() => {
                  const retentionRates: Record<string, number> = {
                    '1': 1.00, '2': 0.50, '3': 1.00, '4': 0.50, '7': 0.50, '8': 0.00,
                  };
                  const rate = retentionRates[wizardData.retention];
                  if (wizardData.retention === '8') {
                    if (wizardData.retentionAmount > 0) {
                      return (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-3 rounded-lg mt-3">
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            <strong>Retención DGI D402:</strong> Monto manual de retención: <strong>B/{wizardData.retentionAmount.toFixed(2)}</strong>
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }
                  if (rate === undefined || rate === 0) return null;
                  const expectedRetention = round2(wizardData.totalTax * rate);
                  return (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-3 rounded-lg mt-3">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <strong>Retención DGI D402:</strong> Monto estimado de retención: <strong>B/{expectedRetention.toFixed(2)}</strong> (ITBMS B/{wizardData.totalTax.toFixed(2)} × {(rate * 100).toFixed(0)}%)
                      </p>
                    </div>
                  );
                })()}
              </div>
            </CardBody>
          </Card>
        </div>

        {/* COLUMNA DERECHA */}
        <div className="space-y-5">
          {/* Grupo: Tiempo de pago */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4">
                📋 Tiempo de pago
              </h3>
              <Select
                label="Tiempo de pago"
                value={wizardData.paymentCondition}
                onChange={handleInputChange('paymentCondition')}
                helperText="Selecciona el tiempo de pago de la factura"
                data-testid="invoice-wizard-step4-payment-condition-select"
              >
                <option value="1">Contado</option>
                <option value="2">Crédito/Plazo</option>
                <option value="3">Mixto</option>
              </Select>
            </CardBody>
          </Card>

          {/* Grupo: Formas de Pago - DGI D30: obligatorio siempre (Ocu 1-10) */}
          {wizardData.paymentCondition === '2' ? (
            // Crédito: forma de pago auto-asignada, solo mostrar resumen informativo
            <Card>
              <CardBody>
                <h3 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4">
                  💳 Forma de Pago
                </h3>
                <div className="p-3 bg-secondary-50 dark:bg-secondary-900 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">Crédito</span>
                    <span className="text-lg font-bold text-secondary-900 dark:text-white">${formatNumber(wizardData.grandTotal)}</span>
                  </div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-2">
                    DGI D30: Para pago a crédito, la forma de pago se asigna automáticamente. Configure las cuotas en la sección de abajo.
                  </p>
                </div>
              </CardBody>
            </Card>
          ) : (
            // Contado o Mixto: formulario completo de formas de pago
            <Card>
              <CardBody>
                <h3 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4">
                  💳 Formas de Pago
                </h3>

                {/* Resumen de pagos */}
                <div className="mb-4 p-3 bg-secondary-50 dark:bg-secondary-900 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                      {isNote
                        ? wizardData.documentMode === 'credit-note'
                          ? 'Total a Reembolsar:'
                          : 'Total Cargo Adicional:'
                        : 'Total Factura:'}
                    </span>
                    <span className="text-lg font-bold text-secondary-900 dark:text-white">${formatNumber(wizardData.grandTotal)}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                      {isNote
                        ? wizardData.documentMode === 'credit-note'
                          ? 'Reembolsado:'
                          : 'Cobrado:'
                        : 'Total Pagado:'}
                    </span>
                    <span className="text-lg font-bold text-success-600">${formatNumber(totalPayments)}</span>
                  </div>
                  {changeAmount === 0 ? (
                    <div className="flex justify-between items-center pt-2 border-t border-secondary-200 dark:border-secondary-700">
                      <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">Pendiente:</span>
                      <span className={`text-lg font-bold ${remainingAmount > 0 ? 'text-warning-600' : 'text-success-600'}`}>
                        ${formatNumber(remainingAmount)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center pt-2 border-t border-secondary-200 dark:border-secondary-700">
                      <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">Vuelto:</span>
                      <span className="text-lg font-bold text-info-600">
                        ${formatNumber(changeAmount)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Lista de pagos agregados */}
                {wizardData.payments && wizardData.payments.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {wizardData.payments.map((payment, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg"
                        data-testid={`invoice-wizard-step4-payment-row-${index}`}
                      >
                        <div className="flex-1">
                          <div className="font-medium text-secondary-900 dark:text-white">
                            {PAYMENT_METHODS.find(pm => pm.value === payment.method)?.label || payment.method}
                          </div>
                          {payment.description && (
                            <div className="text-xs text-secondary-500 dark:text-secondary-400">{payment.description}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-primary-600">${formatNumber(payment.amount)}</span>
                          <button
                            onClick={() => handleRemovePayment(index)}
                            className="text-danger-600 hover:text-danger-800"
                            title="Eliminar"
                            data-testid={`invoice-wizard-step4-remove-payment-button-${index}`}
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Agregar nuevo pago */}
                {remainingAmount > 0 && (
                  <div className="space-y-3 p-3 border-2 border-dashed border-primary-300 dark:border-primary-700 rounded-lg">
                    <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
                      Agregar Forma de Pago
                    </p>
                    <Select
                      label="Forma de Pago"
                      value={newPaymentMethod}
                      onChange={(e) => setNewPaymentMethod(e.target.value)}
                      data-testid="invoice-wizard-step4-new-payment-method-select"
                    >
                      {PAYMENT_METHODS.map(method => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </Select>
                    <Input
                      label="Monto"
                      type="number"
                      value={newPaymentAmount}
                      onChange={(e) => setNewPaymentAmount(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      max={isNote || newPaymentMethod !== '02' ? remainingAmount : undefined}
                      step="0.01"
                      helperText={
                        isNote
                          ? `${wizardData.documentMode === 'credit-note' ? 'Reembolso' : 'Cargo adicional'}: $${formatNumber(remainingAmount)} (monto exacto)`
                          : newPaymentMethod === '02'
                          ? `Pendiente: $${formatNumber(remainingAmount)} (puede exceder para calcular vuelto)`
                          : `Máximo: $${formatNumber(remainingAmount)}`
                      }
                      data-testid="invoice-wizard-step4-new-payment-amount-input"
                    />
                    {/* Solo mostrar descripción cuando el método de pago es "99" (Otro) */}
                    {newPaymentMethod === '99' && (
                      <Input
                        label="Descripción (Requerida)"
                        value={newPaymentDescription}
                        onChange={(e) => setNewPaymentDescription(e.target.value)}
                        placeholder="Especifique el método de pago (mínimo 10 caracteres)"
                        maxLength={100}
                        required
                        helperText={
                          newPaymentDescription.trim().length > 0 && newPaymentDescription.trim().length < 10
                            ? `Faltan ${10 - newPaymentDescription.trim().length} caracteres`
                            : newPaymentDescription.trim().length >= 10
                            ? `✓ ${newPaymentDescription.trim().length} caracteres`
                            : 'Ingrese al menos 10 caracteres'
                        }
                        error={
                          newPaymentDescription.trim().length > 0 && newPaymentDescription.trim().length < 10
                            ? 'La descripción debe tener al menos 10 caracteres'
                            : undefined
                        }
                        data-testid="invoice-wizard-step4-new-payment-description-input"
                      />
                    )}
                    <Button
                      variant="primary"
                      onClick={handleAddPayment}
                      className="w-full"
                      disabled={!newPaymentAmount || parseFloat(newPaymentAmount) <= 0}
                      data-testid="invoice-wizard-step4-add-payment-button"
                    >
                      <PlusIcon className="w-5 h-5 mr-2" />
                      Agregar Pago
                    </Button>
                  </div>
                )}

                {remainingAmount <= 0 && (
                  <div className="p-3 bg-success-50 dark:bg-success-900/20 rounded-lg text-center">
                    <p className="text-sm font-medium text-success-700 dark:text-success-300">
                      {isNote
                        ? wizardData.documentMode === 'credit-note'
                          ? '✓ Reembolso completo'
                          : '✓ Cargo adicional completo'
                        : '✓ Factura pagada completamente'}
                      {changeAmount > 0 && !isNote && ` (Vuelto: $${formatNumber(changeAmount)})`}
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Grupo: Notas — información adicional que aparece en el CAFE */}
          <Card>
            <CardBody>
              <h3 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4">
                Notas
              </h3>
              <div className="w-full">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Información adicional acerca de la factura
                </label>
                <textarea
                  className="block w-full rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400 dark:placeholder-secondary-500 focus:border-primary-500 focus:ring-primary-500"
                  rows={3}
                  defaultValue={wizardData.notes || ''}
                  onBlur={(e) => onUpdate({ notes: e.target.value })}
                  placeholder="Ej: Número de seguro, observaciones, instrucciones especiales..."
                  maxLength={5000}
                  data-testid="invoice-wizard-step4-notes-textarea"
                />
                <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
                  Esta nota aparecerá en el documento CAFE generado
                </p>
              </div>
            </CardBody>
          </Card>

          {/* Grupo: Información de pago a plazo (solo si paymentCondition != "1") */}
          {wizardData.paymentCondition !== '1' && (
            <Card>
              <CardBody>
                <h3 className="text-sm font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-4">
                  📅 Información de pago a plazo
                </h3>
                <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-4">
                  {wizardData.paymentCondition === '3'
                    ? 'Define las cuotas y fechas de pago. El total de las cuotas debe coincidir con el saldo pendiente.'
                    : 'Define las cuotas y fechas de pago. El total de las cuotas debe coincidir con el total de la factura.'}
                </p>

                {/* Calcular totales */}
                {(() => {
                  const totalTerms = (wizardData.paymentTerms || []).reduce(
                    (sum, term) => sum + parseFloat(term.dValItPlazo),
                    0
                  );
                  // Para Mixto (3): las cuotas deben sumar el saldo pendiente (grandTotal - totalPayments)
                  // Para Crédito (2): las cuotas deben sumar el total de la factura
                  const targetAmount = wizardData.paymentCondition === '3' ? remainingAmount : wizardData.grandTotal;
                  const difference = Math.abs(totalTerms - targetAmount);
                  const isBalanced = difference < 0.01; // Tolerancia de 1 centavo

                  return (
                    <div className="mb-4 p-3 bg-secondary-50 dark:bg-secondary-900 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                          Total Cuotas:
                        </span>
                        <span className="text-lg font-bold text-secondary-900 dark:text-white">
                          ${formatNumber(totalTerms)}
                        </span>
                      </div>
                      {/* Para Mixto: mostrar el saldo pendiente como objetivo */}
                      {wizardData.paymentCondition === '3' && (
                        <>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                              Total Factura:
                            </span>
                            <span className="text-lg font-bold text-secondary-600">
                              ${formatNumber(wizardData.grandTotal)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                              Pagado al contado:
                            </span>
                            <span className="text-lg font-bold text-success-600">
                              ${formatNumber(totalPayments)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                              Saldo a financiar:
                            </span>
                            <span className="text-lg font-bold text-primary-600">
                              ${formatNumber(targetAmount)}
                            </span>
                          </div>
                        </>
                      )}
                      {/* Para Crédito: mostrar solo el total de la factura */}
                      {wizardData.paymentCondition === '2' && (
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                            Total Factura:
                          </span>
                          <span className="text-lg font-bold text-primary-600">
                            ${formatNumber(targetAmount)}
                          </span>
                        </div>
                      )}
                      {!isBalanced && (
                        <div className="flex justify-between items-center pt-2 border-t border-secondary-200 dark:border-secondary-700">
                          <span className="text-sm font-medium text-warning-700 dark:text-warning-300">
                            {totalTerms < targetAmount ? 'Falta:' : 'Excede:'}
                          </span>
                          <span className="text-lg font-bold text-warning-600">
                            ${formatNumber(difference)}
                          </span>
                        </div>
                      )}
                      {isBalanced && (
                        <div className="pt-2 border-t border-secondary-200 dark:border-secondary-700">
                          <p className="text-sm font-medium text-success-600 text-center">
                            {wizardData.paymentCondition === '3'
                              ? '✓ Las cuotas coinciden con el saldo pendiente'
                              : '✓ Las cuotas coinciden con el total de la factura'}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Lista de plazos agregados */}
                {wizardData.paymentTerms && wizardData.paymentTerms.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {wizardData.paymentTerms.map((term, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg"
                        data-testid={`invoice-wizard-step4-payment-term-row-${index}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-primary-600 rounded-full">
                              {term.dSecItem}
                            </span>
                            <span className="font-medium text-secondary-900 dark:text-white">
                              {new Date(term.dFecItPlazo).toLocaleDateString('es-PA')}
                            </span>
                          </div>
                          {term.dInfPagPlazo && (
                            <div className="text-xs text-secondary-500 dark:text-secondary-400 ml-8">
                              {term.dInfPagPlazo}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-primary-600">${formatNumber(parseFloat(term.dValItPlazo))}</span>
                          <button
                            onClick={() => handleRemovePaymentTerm(index)}
                            className="text-danger-600 hover:text-danger-800"
                            title="Eliminar"
                            data-testid={`invoice-wizard-step4-remove-payment-term-button-${index}`}
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Agregar nuevo plazo */}
                <div className="space-y-3 p-3 border-2 border-dashed border-primary-300 dark:border-primary-700 rounded-lg">
                  <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
                    Agregar Cuota
                  </p>
                  <Input
                    label="Fecha de Pago"
                    type="date"
                    value={newTermDate}
                    onChange={(e) => setNewTermDate(e.target.value)}
                    helperText="Fecha en que vence esta cuota"
                    data-testid="invoice-wizard-step4-new-term-date-input"
                  />
                  <Input
                    label="Monto de la Cuota"
                    type="text"
                    inputMode="decimal"
                    value={newTermAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty, numbers, and decimal point
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setNewTermAmount(value);
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      if (value > 0) {
                        setNewTermAmount(value.toFixed(2));
                      }
                    }}
                    placeholder="0.00"
                    helperText="Valor de esta cuota"
                    data-testid="invoice-wizard-step4-new-term-amount-input"
                  />
                  <Input
                    label="Descripción (Opcional)"
                    value={newTermDescription}
                    onChange={(e) => setNewTermDescription(e.target.value)}
                    placeholder={`Cuota de plazo ${(wizardData.paymentTerms || []).length + 1}`}
                    maxLength={1000}
                    helperText="Mínimo 15 caracteres si se informa (DGI D504). Si no se especifica, se generará automáticamente."
                    data-testid="invoice-wizard-step4-new-term-description-input"
                  />
                  <Button
                    variant="primary"
                    onClick={handleAddPaymentTerm}
                    className="w-full"
                    disabled={!newTermDate || !newTermAmount || parseFloat(newTermAmount) <= 0}
                    data-testid="invoice-wizard-step4-add-payment-term-button"
                  >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Agregar Cuota
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}

        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious} data-testid="invoice-wizard-step4-previous-button">
          Anterior
        </Button>
        <Button
          variant="primary"
          onClick={handleNext}
          data-testid="invoice-wizard-step4-submit-button"
          disabled={(() => {
            // Validación según el tipo de pago:
            // - Contado (1): debe haber pagos y no debe haber pendiente
            // - Crédito (2): NO hay pagos directos, solo cuotas que sumen el total de la factura
            // - Mixto (3): debe haber pagos y cuotas que sumen el saldo pendiente

            if (wizardData.paymentCondition === '1') {
              // Contado: debe haber pagos y no debe haber pendiente.
              // Excepción: si el total de la factura es $0.00, ya está "pagada" trivialmente
              // y no se debe exigir que el usuario agregue una forma de pago.
              if (grandTotalRounded > 0 && totalPayments === 0) return true;
              if (remainingAmount > 0) return true;
            } else if (wizardData.paymentCondition === '2') {
              // Crédito: las cuotas deben sumar el total de la factura
              const totalTerms = (wizardData.paymentTerms || []).reduce(
                (sum, term) => sum + parseFloat(term.dValItPlazo),
                0
              );
              const difference = Math.abs(totalTerms - wizardData.grandTotal);
              if (difference >= 0.01) return true;
              if (!wizardData.paymentTerms || wizardData.paymentTerms.length === 0) return true;
            } else if (wizardData.paymentCondition === '3') {
              // Mixto: debe haber pagos y cuotas que sumen el saldo pendiente
              if (totalPayments === 0) return true;
              const totalTerms = (wizardData.paymentTerms || []).reduce(
                (sum, term) => sum + parseFloat(term.dValItPlazo),
                0
              );
              const difference = Math.abs(totalTerms - remainingAmount);
              if (difference >= 0.01) return true;
              if (!wizardData.paymentTerms || wizardData.paymentTerms.length === 0) return true;
            }

            return false;
          })()}
          title={(() => {
            if (wizardData.paymentCondition === '1') {
              // Contado
              if (grandTotalRounded > 0 && totalPayments === 0) return 'Debe agregar al menos una forma de pago';
              if (remainingAmount > 0) {
                return isNote
                  ? wizardData.documentMode === 'credit-note'
                    ? `Falta definir reembolso: $${formatNumber(remainingAmount)}`
                    : `Falta definir cargo: $${formatNumber(remainingAmount)}`
                  : `Falta pagar $${formatNumber(remainingAmount)}`;
              }
              if (changeAmount > 0 && !isNote) return `Vuelto: $${formatNumber(changeAmount)}`;
            } else if (wizardData.paymentCondition === '2') {
              // Crédito
              if (!wizardData.paymentTerms || wizardData.paymentTerms.length === 0) {
                return 'Debe agregar cuotas de pago a plazo';
              }
              const totalTerms = (wizardData.paymentTerms || []).reduce(
                (sum, term) => sum + parseFloat(term.dValItPlazo),
                0
              );
              const difference = Math.abs(totalTerms - wizardData.grandTotal);
              if (difference >= 0.01) {
                return `Las cuotas ($${formatNumber(totalTerms)}) deben sumar el total de la factura ($${formatNumber(wizardData.grandTotal)})`;
              }
            } else if (wizardData.paymentCondition === '3') {
              // Mixto
              if (totalPayments === 0) return 'Debe agregar al menos una forma de pago';
              if (!wizardData.paymentTerms || wizardData.paymentTerms.length === 0) {
                return 'Debe agregar cuotas de pago a plazo';
              }
              const totalTerms = (wizardData.paymentTerms || []).reduce(
                (sum, term) => sum + parseFloat(term.dValItPlazo),
                0
              );
              const difference = Math.abs(totalTerms - remainingAmount);
              if (difference >= 0.01) {
                return `Las cuotas ($${formatNumber(totalTerms)}) deben sumar el saldo pendiente ($${formatNumber(remainingAmount)})`;
              }
              if (changeAmount > 0 && !isNote) return `Vuelto: $${formatNumber(changeAmount)}`;
            }

            return '';
          })()}
        >
          Revisar y Enviar
        </Button>
      </div>
    </div>
  );
}
